import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import SuccessModal from './SuccessModal';

const UploadFilesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 12px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  width: 100%;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #6D6D71;
`;

const ContentBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 20px;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C5C5C6;
  border-radius: 12px;
`;

const FileUploadWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  width: 100%;
  height: 56px;
  background: #FFFFFF;
  border: 1px solid #F0F0F0;
  border-radius: 12px;
  opacity: ${props => props.isLoading ? 0.6 : 1};
  pointer-events: ${props => props.isLoading ? 'none' : 'auto'};
`;

const FileUploadText = styled.span`
  font-family: 'Sofia Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #6D6D71;
`;

const FileUploadIconWrapper = styled.label`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 8px;
  width: 32px;
  height: 32px;
  border: 1px solid #A7A7AA;
  border-radius: 4px;
  cursor: pointer;
`;

const FileUploadIcon = styled.div`
  width: 16px;
  height: 16px;
  border: solid 2px #6D6D71;
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
  margin-top: -4px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileList = styled.ul`
  list-style-type: none;
  padding: 0;
  width: 100%;
`;

const FileItem = styled.li`
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
  margin-bottom: 8px;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #2575BD;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ConfirmationModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const ModalTitle = styled.h3`
  font-family: 'Sofia Sans', sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #2C2C2D;
  margin-bottom: 16px;
`;

const ModalText = styled.p`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 14px;
  color: #6D6D71;
  margin-bottom: 24px;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ModalButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  background: ${props => props.primary ? '#2575BD' : '#f5f5f5'};
  color: ${props => props.primary ? 'white' : '#6D6D71'};
  
  &:hover {
    background: ${props => props.primary ? '#1c5c94' : '#e0e0e0'};
  }
`;

const StatusIndicator = styled.span`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.success ? '#28a745' : props.error ? '#dc3545' : '#6D6D71'};
  margin-left: 8px;
`;

const FileActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ViewFileButton = styled.button`
  padding: 4px 8px;
  background: #f0f0f0;
  border: 1px solid #c5c5c6;
  border-radius: 4px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: #6D6D71;
  cursor: pointer;
  
  &:hover {
    background: #e0e0e0;
  }
`;

const UploadFiles = ({ participantId, onSaveSuccess }) => {
  const [files, setFiles] = useState({
    ecg_file: null,
    echocardiogram_file: null
  });
  const [uploadedFiles, setUploadedFiles] = useState({
    ecg_file: null,
    echocardiogram_file: null
  });
  const [loadingStates, setLoadingStates] = useState({
    ecg_file: false,
    echocardiogram_file: false
  });
  const [uploadStatus, setUploadStatus] = useState({
    ecg_file: null,
    echocardiogram_file: null
  });
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    fileType: null,
    file: null
  });
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    message: ''
  });

  const handleFileChange = (event, type) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Check file size (1 GB = 1024 * 1024 * 1024 bytes)
      const maxSize = 1024 * 1024 * 1024; // 1 GB
      if (selectedFile.size > maxSize) {
        setSuccessModal({
          isOpen: true,
          message: `File is too large. Maximum file size allowed is 1 GB. Your file is ${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB.`
        });
        // Reset the input
        event.target.value = '';
        return;
      }

      setConfirmationModal({
        isOpen: true,
        fileType: type,
        file: selectedFile
      });
    }
  };

  const confirmFileUpload = async () => {
    const { fileType, file } = confirmationModal;
    setConfirmationModal({ isOpen: false, fileType: null, file: null });
    
    // Set the file in state
    setFiles(prevFiles => ({
      ...prevFiles,
      [fileType]: file
    }));

    // Start loading state
    setLoadingStates(prev => ({ ...prev, [fileType]: true }));
    setUploadStatus(prev => ({ ...prev, [fileType]: null }));

    try {
      // Upload using presigned URL
      const url = await uploadWithPresignedUrl(file, fileType);
      
      // Update MongoDB
      await updateMongoDBWithFileUrl(fileType, url);
      
      // Update states
      setUploadedFiles(prev => ({ ...prev, [fileType]: url }));
      setUploadStatus(prev => ({ ...prev, [fileType]: 'success' }));
      
      setSuccessModal({
        isOpen: true,
        message: `${fileType.replace('_', ' ').toUpperCase()} uploaded successfully!`
      });
      
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus(prev => ({ ...prev, [fileType]: 'error' }));
      setSuccessModal({
        isOpen: true,
        message: `Error uploading ${fileType.replace('_', ' ')}. Please try again.`
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const cancelFileUpload = () => {
    setConfirmationModal({ isOpen: false, fileType: null, file: null });
  };

  const uploadWithPresignedUrl = async (file, type) => {
    const token = localStorage.getItem('token');
    
    try {
      // Get presigned POST data from backend
      const presignedResponse = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}participant/get-presigned-url/`,
        {
          participant_id: participantId,
          file_type: type,
          file_name: file.name,
          content_type: file.type
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { presigned_post, file_url } = presignedResponse.data;

      // Create FormData for POST upload
      const formData = new FormData();
      
      // Add all the fields from presigned POST
      Object.keys(presigned_post.fields).forEach(key => {
        formData.append(key, presigned_post.fields[key]);
      });
      
      // Add the file last
      formData.append('file', file);

      // Upload file directly to S3 using presigned POST
      await axios.post(presigned_post.url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return file_url;
    } catch (error) {
      console.error('Error uploading with presigned POST:', error);
      
      // Handle specific S3 errors
      if (error.response) {
        const errorData = error.response.data;
        
        // Parse XML error response from S3
        if (typeof errorData === 'string' && errorData.includes('<Error>')) {
          if (errorData.includes('<Code>EntityTooLarge</Code>')) {
            throw new Error('File is too large. Maximum file size allowed is 1 GB.');
          } else if (errorData.includes('<Code>InvalidArgument</Code>')) {
            throw new Error('Invalid file type. Please check the file format.');
          } else if (errorData.includes('<Code>AccessDenied</Code>')) {
            throw new Error('Access denied. Please check your permissions.');
          } else if (errorData.includes('<Code>SignatureDoesNotMatch</Code>')) {
            throw new Error('Upload session expired. Please try again.');
          } else {
            // Extract error message from XML if possible
            const messageMatch = errorData.match(/<Message>(.*?)<\/Message>/);
            const message = messageMatch ? messageMatch[1] : 'Upload failed';
            throw new Error(`Upload failed: ${message}`);
          }
        } else {
          throw new Error(`Upload failed with status ${error.response.status}: ${error.response.statusText}`);
        }
      } else if (error.request) {
        throw new Error('Network error. Please check your internet connection and try again.');
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  };

  const updateMongoDBWithFileUrl = async (fileType, url) => {
    const token = localStorage.getItem('token');
    
    // Map file types to their correct endpoints
    const endpointMap = {
      ecg_file: 'ecg',
      echocardiogram_file: 'echocardiogram',
      phonocardiogram_file: 'phonocardiogram',
      consent_file: 'consent'
    };
    
    const fileTypeForEndpoint = endpointMap[fileType];
    
    await axios.put(
      `${process.env.REACT_APP_BACKEND_URL}participant/upload/${fileTypeForEndpoint}/`,
      { 
        participant_id: participantId,
        file_url: url
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  const handleSuccessModalClose = () => {
    setSuccessModal({ isOpen: false, message: '' });
  };

  const fetchExistingFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}participant/get-participant-info?participant_id=${participantId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const participantData = response.data;
      
      // Update uploadedFiles state with existing file URLs
      const existingFiles = {
        ecg_file: participantData.ecg_file_url || null,
        echocardiogram_file: participantData.echocardiogram_file_url || null
      };

      setUploadedFiles(existingFiles);

      // Set upload status to success for existing files
      const existingStatus = {};
      Object.entries(existingFiles).forEach(([key, url]) => {
        existingStatus[key] = url ? 'success' : null;
      });
      setUploadStatus(existingStatus);

    } catch (error) {
      console.error('Error fetching existing files:', error);
    }
  };

  useEffect(() => {
    if (participantId) {
      fetchExistingFiles();
    }
  }, [participantId]);

  const handleViewFile = (fileUrl) => {
    window.open(fileUrl, '_blank');
  };

  return (
    <UploadFilesContainer>
      {Object.entries(files).map(([type, file]) => (
        <SectionContainer key={type}>
          <SectionTitle>{type.replace('_', ' ').toUpperCase()}</SectionTitle>
          <ContentBox>
            <FileUploadWrapper isLoading={loadingStates[type]}>
              <FileUploadText>
                {file ? `${file.name}` : 
                 uploadedFiles[type] ? 
                   `${uploadedFiles[type].split('/').pop()}` : 
                   `Choose ${type.replace('_', ' ')}`}
                {loadingStates[type] && (
                  <StatusIndicator>Uploading...</StatusIndicator>
                )}
                {uploadStatus[type] === 'success' && (
                  <StatusIndicator success>✓ Uploaded</StatusIndicator>
                )}
                {uploadStatus[type] === 'error' && (
                  <StatusIndicator error>✗ Failed</StatusIndicator>
                )}
              </FileUploadText>
              <FileActions>
                {uploadedFiles[type] && !loadingStates[type] && (
                  <ViewFileButton onClick={() => handleViewFile(uploadedFiles[type])}>
                    View
                  </ViewFileButton>
                )}
                {loadingStates[type] && <LoadingSpinner />}
                <FileUploadIconWrapper htmlFor={`${type}-input`}>
                  <FileUploadIcon />
                  <FileInput
                    type="file"
                    accept={type === 'consent_file' ? '.pdf' : undefined}
                    onChange={(e) => handleFileChange(e, type)}
                    id={`${type}-input`}
                    disabled={loadingStates[type]}
                  />
                </FileUploadIconWrapper>
              </FileActions>
            </FileUploadWrapper>
          </ContentBox>
        </SectionContainer>
      ))}

      {/* Confirmation Modal */}
      {confirmationModal.isOpen && (
        <ConfirmationModal>
          <ModalContent>
            <ModalTitle>Confirm File Upload</ModalTitle>
            <ModalText>
              Are you sure you want to upload "{confirmationModal.file?.name}" as {confirmationModal.fileType?.replace('_', ' ')}?
              <br />
              {uploadedFiles[confirmationModal.fileType] && (
                <small style={{ color: '#dc3545' }}>
                  This will replace the existing file.
                </small>
              )}
              {!uploadedFiles[confirmationModal.fileType] && (
                <small>This will immediately upload the file to cloud storage.</small>
              )}
            </ModalText>
            <ModalButtons>
              <ModalButton onClick={cancelFileUpload}>Cancel</ModalButton>
              <ModalButton primary onClick={confirmFileUpload}>Upload</ModalButton>
            </ModalButtons>
          </ModalContent>
        </ConfirmationModal>
      )}

      {/* Success Modal */}
      {successModal.isOpen && (
        <SuccessModal
          isOpen={successModal.isOpen}
          onClose={handleSuccessModalClose}
          title="Upload Status"
          message={successModal.message}
        />
      )}
    </UploadFilesContainer>
  );
};

export default UploadFiles;
